import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from '@markroom/ui';
import type { AppConfig, FileSource } from '@markroom/ui';
import { isMobilePlatform, isTauri } from './platform/detect';
import { createWebFileSource } from './platform/web';
import './styles.css';

// Native has no local server, so feedback posts to the deployed web proxy.
// Overridable per build via VITE_FEEDBACK_ENDPOINT — keep the Tauri CSP
// connect-src (tauri.conf.json) in sync with whatever host this points at.
const NATIVE_FEEDBACK_ENDPOINT =
  import.meta.env.VITE_FEEDBACK_ENDPOINT ?? 'https://app.markroom.net/api/feedback';

function appConfig(): AppConfig {
  if (isTauri()) {
    // Turnstile validates the page domain, so it's web-only (no site key here).
    return { feedbackEndpoint: NATIVE_FEEDBACK_ENDPOINT, turnstileSiteKey: '' };
  }
  return {
    feedbackEndpoint: '/api/feedback',
    turnstileSiteKey: import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '',
  };
}

async function selectFileSource(): Promise<FileSource> {
  if (isTauri()) {
    if (isMobilePlatform()) {
      // content:// (Android) + security-scoped URLs (iOS), code-split.
      const { createTauriMobileFileSource } = await import('./platform/tauri-mobile');
      return createTauriMobileFileSource();
    }
    // Code-split so Tauri plugin imports never ship in the static web bundle.
    const { createTauriDesktopFileSource } = await import('./platform/tauri-desktop');
    return createTauriDesktopFileSource();
  }
  return createWebFileSource();
}

async function bootstrap(): Promise<void> {
  const source = await selectFileSource();
  const rootEl = document.getElementById('root');
  if (!rootEl) throw new Error('Root element #root not found');
  createRoot(rootEl).render(
    <StrictMode>
      <App source={source} config={appConfig()} />
    </StrictMode>,
  );
}

void bootstrap();
